import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useCreateVndlyCandidateMutation } from 'queries/admin/vndly/useCreateVndlyCandidateMutation'
import { useLinkVndlyCandidateMutation } from 'queries/admin/vndly/useLinkVndlyCandidateMutation'
import { useVndlyCandidateIssuesPaginationQueryState } from '../hooks/useVndlyCandidateIssuesPaginationQueryState'
import { useVndlyCandidateIssuesQuery } from 'queries/admin/vndly/useVndlyCandidateIssuesQuery'
import { useVndlyIssuesFiltersQueryState } from 'pages/VndlyIssues/hooks/useVndlyIssuesFiltersQueryState'

type VndlyCandidateIssuesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useVndlyCandidateIssuesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
    createVndlyCandidateMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
    linkVndlyCandidateMutationState: {
      isPending: boolean
      isError: boolean
      isSuccess: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<
      typeof useVndlyCandidateIssuesPaginationQueryState
    >[1]
    createVndlyCandidate: ReturnType<
      typeof useCreateVndlyCandidateMutation
    >['mutate']
    resetCreateVndlyCandidate: ReturnType<
      typeof useCreateVndlyCandidateMutation
    >['reset']
    linkVndlyCandidate: ReturnType<
      typeof useLinkVndlyCandidateMutation
    >['mutate']
    resetLinkVndlyCandidate: ReturnType<
      typeof useLinkVndlyCandidateMutation
    >['reset']
  }
}

const [Context, useVndlyCandidateIssuesContextValue] =
  createContext<VndlyCandidateIssuesContextState>({
    name: 'VndlyCandidateIssuesContext',
  })

export const VndlyCandidateIssuesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters] = useVndlyIssuesFiltersQueryState()

  const [pagination, setPagination] =
    useVndlyCandidateIssuesPaginationQueryState()

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useVndlyCandidateIssuesQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    instanceIds: filters.instanceIds,
    companyIds: filters.companyIds,
  })

  const {
    mutate: createVndlyCandidate,
    isPending: isCreateVndlyCandidatePending,
    isError: isCreateVndlyCandidateError,
    isSuccess: isCreateVndlyCandidateSuccess,
    error: createVndlyCandidateError,
    reset: resetCreateVndlyCandidate,
  } = useCreateVndlyCandidateMutation()

  const {
    mutate: linkVndlyCandidate,
    isPending: isLinkVndlyCandidatePending,
    isError: isLinkVndlyCandidateError,
    isSuccess: isLinkVndlyCandidateSuccess,
    error: linkVndlyCandidateError,
    reset: resetLinkVndlyCandidate,
  } = useLinkVndlyCandidateMutation()

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    // eslint-disable-next-line no-console
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
      createVndlyCandidateMutationState: {
        isPending: isCreateVndlyCandidatePending,
        isError: isCreateVndlyCandidateError,
        isSuccess: isCreateVndlyCandidateSuccess,
        errorMessage: parseErrorMessage(createVndlyCandidateError),
      },
      linkVndlyCandidateMutationState: {
        isPending: isLinkVndlyCandidatePending,
        isError: isLinkVndlyCandidateError,
        isSuccess: isLinkVndlyCandidateSuccess,
        errorMessage: parseErrorMessage(linkVndlyCandidateError),
      },
    },
    actions: {
      refetch,
      setPagination,
      createVndlyCandidate,
      resetCreateVndlyCandidate,
      linkVndlyCandidate,
      resetLinkVndlyCandidate,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useVndlyCandidateIssuesContextValue }
